/**
 * History section (inside `CapabilityDrawer`) — a collapsed-by-default dot
 * timeline of applied revisions (`changeKind !== 'proposal_rejected'`),
 * newest first. Collapsed by default because the header's "Last edited
 * by…" line already surfaces the one fact from this list worth seeing at a
 * glance; expanding gets the full history.
 *
 * @module features/product-map/components/CapabilityHistorySection
 */

import { formatDistanceToNow } from 'date-fns';
import { CollapsibleSection, Text } from '@chatsdet/ui';
import { revisionActorName } from '../productMapLogic';
import type { ProductMapRevision } from '../types';

const CHANGE_KIND_LABEL: Record<string, string> = {
  created: 'created',
  updated: 'updated',
  deprecated: 'deprecated',
  recoded: 're-coded',
  proposal_rejected: 'proposed a locked change',
};

/** `created`/`updated`/`deprecated` phrased for a rule row, e.g. "rule added: <statement>". */
const RULE_CHANGE_KIND_LABEL: Record<string, string> = {
  created: 'added',
  updated: 'updated',
  deprecated: 'removed',
};

/** Rule revisions ride alongside the capability's own (see `getCapabilityDetail`) — distinguish them with the statement, since "updated" alone doesn't say what. */
function describeRevision(revision: ProductMapRevision): string {
  if (revision.entityType === 'rule' && revision.changeKind !== 'proposal_rejected') {
    const statement = typeof revision.snapshotJson.statement === 'string' ? revision.snapshotJson.statement : null;
    const verb = RULE_CHANGE_KIND_LABEL[revision.changeKind] ?? revision.changeKind;
    return statement ? `rule ${verb}: "${statement}"` : `rule ${verb}`;
  }
  return CHANGE_KIND_LABEL[revision.changeKind] ?? revision.changeKind;
}

type Props = {
  /** Applied revisions (already excludes `proposal_rejected`), oldest first. */
  appliedRevisions: ProductMapRevision[];
};

export function CapabilityHistorySection({ appliedRevisions }: Props) {
  if (appliedRevisions.length === 0) return null;

  return (
    <CollapsibleSection label="History" count={appliedRevisions.length}>
      <div className="relative ml-3">
        <div className="absolute bottom-2 left-0 top-2 w-px bg-border" />
        <div className="space-y-3">
          {[...appliedRevisions].reverse().map((revision) => (
            <div key={revision.id} className="relative pl-6">
              <div className="absolute left-[-3px] top-[7px] h-[7px] w-[7px] rounded-full bg-muted-foreground/50 ring-2 ring-background" />
              <Text size="sm">
                <span className="font-medium">{revisionActorName(revision)}</span> {describeRevision(revision)}
                <span className="text-muted-foreground" title={new Date(revision.createdAt).toISOString()}>
                  {' '}
                  · {formatDistanceToNow(new Date(revision.createdAt))} ago
                </span>
              </Text>
            </div>
          ))}
        </div>
      </div>
    </CollapsibleSection>
  );
}
